/**
 * Onboarding Menu Component
 *
 * Help menu component for manual tour triggering and progress display.
 * Shows available tours, progress indicators, and provides restart/skip options.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  SkipNext as SkipIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as InProgressIcon,
} from "@mui/icons-material";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { useAuth } from "../../contexts/auth/authContext";
import {
  filterPlatformTours,
  getLabMissionTourSlugs,
  isLabMissionTour,
} from "../../../shared/onboarding/tourCatalog";
import { findResumableLabMission } from "../../../shared/onboarding/missionSessionStorage";
import { LearningLabMenuSection } from "../learning-lab/LearningLabMenuSection";
import { useLearningLabNav } from "../learning-lab/useLearningLabNav";
import { logger } from "../../utils/logger";
import { coerceTourPlainText } from "../../../shared/utils/coerceEventListingText";

/**
 * Onboarding Menu Props
 */
interface OnboardingMenuProps {
  /** Anchor element for menu positioning */
  anchorEl: HTMLElement | null;
  /** Whether menu is open */
  open: boolean;
  /** Callback when menu closes */
  onClose: () => void;
}

/**
 * Onboarding Menu Component
 */
export const OnboardingMenu: React.FC<OnboardingMenuProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const {
    state,
    getAllTours,
    getAvailableTours,
    startTour,
    skipTour,
    resetOnboarding,
    isFlowCompleted,
  } = useOnboarding();
  const { user } = useAuth();
  const { navState } = useLearningLabNav();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const menuPaperRef = useRef<HTMLDivElement>(null);

  const platformAvailableTours = useMemo(
    () => filterPlatformTours(getAvailableTours()),
    [getAvailableTours],
  );

  const completionPercentage = state.progress
    ? state.progress.completionPercentage
    : 0;

  const recommendedTours = platformAvailableTours.filter(
    (t) =>
      (t.tourType === "welcome" || t.tourType === "task" || !t.tourType) &&
      !isFlowCompleted(t.id),
  );
  const referenceTours = platformAvailableTours.filter(
    (t) => t.tourType === "reference" && !isFlowCompleted(t.id),
  );
  const completedPlatformTours = platformAvailableTours.filter((t) =>
    isFlowCompleted(t.id),
  );
  const isOffline = typeof navigator !== "undefined" && !navigator.onLine;

  const resumePlatformTour = platformAvailableTours.find((tour) => {
    const started = (state.progress?.startedFlows || []).includes(tour.id);
    const completed = isFlowCompleted(tour.id);
    const skipped = (state.progress?.skippedFlows || []).includes(tour.id);
    return started && !completed && !skipped;
  });

  const resumeLabMissionSlug = useMemo(() => {
    if (!user?.id) {
      return null;
    }
    return findResumableLabMission(user.id, getLabMissionTourSlugs());
  }, [user?.id]);

  const resumeLabMission = useMemo(() => {
    if (!resumeLabMissionSlug) {
      return null;
    }
    return getAllTours().find((t) => t.id === resumeLabMissionSlug) ?? null;
  }, [getAllTours, resumeLabMissionSlug]);

  const renderTourItem = (tour: (typeof platformAvailableTours)[number]) => {
    const isCompleted = isFlowCompleted(tour.id);
    const isActive = state.currentFlowId === tour.id;

    return (
      <MenuItem
        key={tour.id}
        data-testid={`onboarding-menu-item-${tour.id}`}
        onClick={() => {
          if (!isActive && !state.isRunning) {
            handleStartTour(tour.id);
          }
        }}
        disabled={isActive || state.isRunning}
      >
        <ListItemIcon>
          {isActive ? (
            <InProgressIcon color="primary" fontSize="small" />
          ) : isCompleted ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <PlayIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={coerceTourPlainText(tour.name, tour.id)}
          secondary={
            tour.estimatedDurationMinutes
              ? `${tour.estimatedDurationMinutes} min`
              : undefined
          }
        />
        {isActive && (
          <Chip label="Active" size="small" color="primary" sx={{ ml: 1 }} />
        )}
        {isCompleted && (
          <Chip label="Done" size="small" color="success" sx={{ ml: 1 }} />
        )}
      </MenuItem>
    );
  };

  const renderSection = (
    title: string,
    tours: typeof platformAvailableTours,
  ) =>
    tours.length > 0 ? (
      <>
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
        </Box>
        {tours.map(renderTourItem)}
      </>
    ) : null;

  /**
   * Handle tour start
   */
  const handleStartTour = async (tourId: string) => {
    // Clear any previous error
    setErrorMessage(null);

    // Don't start if already running
    if (state.isRunning) {
      setErrorMessage(
        "A tour is already running. Please finish or skip the current tour first.",
      );
      return;
    }

    try {
      await startTour(tourId);
      onClose();
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Failed to start tour";
      logger.error("Error starting tour from menu", { error, tourId });
      setErrorMessage(errorMsg);
    }
  };

  /**
   * Handle reset confirmation
   */
  const handleResetConfirm = async () => {
    try {
      await resetOnboarding();
      setResetDialogOpen(false);
      onClose();
    } catch (error) {
      logger.error("Error resetting onboarding", { error });
      setErrorMessage("Failed to reset onboarding progress. Please try again.");
    }
  };

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target) return;

      // Don't close if clicking on the anchor element
      if (anchorEl && anchorEl.contains(target)) {
        return;
      }

      // Don't close if clicking inside the menu paper
      if (menuPaperRef.current && menuPaperRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on other dialogs/modals
      if (target.closest(".MuiDialog-root")) {
        return;
      }

      // Close if clicking outside - this includes backdrop clicks
      handleClose();
    };

    // Use mousedown to catch the event earlier
    // Add a small delay to avoid immediate closure when menu opens
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl, handleClose]);

  const showResumeDivider =
    !state.isRunning &&
    (resumePlatformTour != null || resumeLabMission != null);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        data-testid="onboarding-menu"
        onClose={() => {
          // Close on any reason (backdrop click, escape key, etc.)
          handleClose();
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        // Ensure menu closes on backdrop click
        disableAutoFocusItem
        PaperProps={{
          ref: menuPaperRef,
          sx: {
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 6,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, minWidth: 300 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Onboarding Tours
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Step-by-step guides. You can pause anytime.
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Platform onboarding progress: {completionPercentage}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
          </Box>
          {errorMessage && (
            <Box sx={{ mt: 1, p: 1, bgcolor: "error.light", borderRadius: 1 }}>
              <Typography variant="caption" color="error.contrastText">
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isOffline && (
            <Box sx={{ mt: 1, p: 1, bgcolor: "warning.light", borderRadius: 1 }}>
              <Typography variant="caption" color="warning.contrastText">
                You&apos;re offline. Progress will sync when you reconnect.
              </Typography>
            </Box>
          )}
        </Box>
        <Divider />

        {resumePlatformTour && !state.isRunning && (
          <MenuItem
            data-testid="onboarding-menu-resume-platform"
            onClick={() => handleStartTour(resumePlatformTour.id)}
          >
            <ListItemIcon>
              <InProgressIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`Resume platform tour: ${coerceTourPlainText(resumePlatformTour.name, resumePlatformTour.id)}`}
              secondary="Pick up where you left off"
            />
          </MenuItem>
        )}

        {resumeLabMission &&
          !state.isRunning &&
          !isLabMissionTour(state.currentFlowId ?? "") && (
            <MenuItem
              data-testid="onboarding-menu-resume-lab"
              onClick={() => handleStartTour(resumeLabMission.id)}
            >
              <ListItemIcon>
                <InProgressIcon color="info" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={`Resume lab mission: ${coerceTourPlainText(resumeLabMission.name, resumeLabMission.id)}`}
                secondary="Continue your Learning Lab practice"
              />
            </MenuItem>
          )}

        {showResumeDivider && <Divider />}

        <LearningLabMenuSection
          variant="menu"
          navState={navState}
          onActionComplete={onClose}
        />

        {platformAvailableTours.length > 0 ? (
          <>
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography variant="overline" color="text.secondary">
                Platform tours
              </Typography>
            </Box>
            {renderSection("Recommended", recommendedTours)}
            {renderSection("Reference", referenceTours)}
            {renderSection("Completed", completedPlatformTours)}
          </>
        ) : (
          <MenuItem disabled>
            <ListItemText primary="No platform tours available" />
          </MenuItem>
        )}

        <Divider />

        {/* Actions */}
        <MenuItem
          onClick={() => {
            if (state.currentFlowId) {
              skipTour();
            }
            onClose();
          }}
          disabled={!state.isRunning}
        >
          <ListItemIcon>
            <SkipIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Skip Current Tour" />
        </MenuItem>

        <MenuItem
          onClick={() => setResetDialogOpen(true)}
          disabled={state.isRunning}
        >
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reset All Progress" />
        </MenuItem>
      </Menu>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Onboarding Progress</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset all onboarding progress? This will
            mark all tours as incomplete and allow you to start over.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetConfirm}
            color="error"
            variant="contained"
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OnboardingMenu;
